import { BrandChip } from '@/components/BrandChip';
import { BrandSearchList } from '@/components/BrandSearchList';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { allBrandsState, allEquipmentTypesState, equipmentTypeFamiliesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { BrandId, EquipmentTypeId, SpaceId } from '@rock/types';
import { familyReach } from '@rock/util';
import { Plus, X } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import type { DocumentScope } from './coverageDraft';
import { FormRow } from './FormRow';
import { EquipmentTypeMultiSelect } from './EquipmentTypeMultiSelect';
import { brandsById } from './modelBrand';

const BRANDS_HINT =
    'The brands this document is for. Its rule reaches only models of a listed brand, and a model linked directly to it under another brand is flagged. A shared nameplate (Whirlpool and KitchenAid selling one refrigerator) lists both.';
const TYPES_HINT =
    'The equipment types this document is for. A model of a listed type fits; one whose type shares a family with a listed type fits too, flagged. A combined volume (furnace and air handler) lists both.';

/** The scope rows' wiring, when a form offers them. */
export interface DocumentScopeFieldsProps {
    value: DocumentScope;
    onChange: (next: DocumentScope) => void;
    /** Brands and types the form must keep (the model a publish links, the document's own on a re-publish). */
    locked?: DocumentScope;
    /** What the document already lists, when it exists: anything beyond it is named as an addition. */
    stored?: DocumentScope;
    /** Orders the type picker. */
    spaceId?: SpaceId;
    saving: boolean;
}

/**
 * The Brands and Types rows of the document form: what a document is FOR, stated once on the document
 * and matched in the tiers model joins use. Each type names its family, since a family widens what the
 * document reaches.
 */
export const DocumentScopeFields: FC<DocumentScopeFieldsProps> = ({
    value,
    onChange,
    locked,
    stored,
    spaceId,
    saving,
}) => {
    const additions = useScopeAdditions(value, stored);
    return (
        <>
            <FormRow label="Brands" hint={BRANDS_HINT}>
                <BrandRows
                    brandIds={value.brandIds}
                    locked={locked?.brandIds ?? []}
                    saving={saving}
                    onChange={(brandIds) => onChange({ ...value, brandIds })}
                />
            </FormRow>
            <FormRow label="Types" hint={TYPES_HINT}>
                <div className="space-y-1">
                    <EquipmentTypeMultiSelect
                        spaceId={spaceId}
                        value={value.typeIds}
                        locked={locked?.typeIds}
                        onChange={(typeIds) => onChange({ ...value, typeIds })}
                        disabled={saving}
                    />
                    <TypeFamilyNotes typeIds={value.typeIds} />
                </div>
            </FormRow>
            {additions && (
                <div className="pl-[calc(7rem+0.75rem)] text-xs text-muted-foreground">
                    Adds {additions} to this document.
                </div>
            )}
        </>
    );
};

const BrandRows: FC<{
    brandIds: BrandId[];
    locked: readonly BrandId[];
    saving: boolean;
    onChange: (next: BrandId[]) => void;
}> = ({ brandIds, locked, saving, onChange }) => {
    const brands = useAsyncAtomValue(allBrandsState, []);
    const byId = useMemo(() => brandsById(brands), [brands]);
    const [pickerOpen, setPickerOpen] = useState(false);
    return (
        <div className="flex flex-wrap items-center gap-1.5">
            {brandIds.map((brandId) => {
                const brand = byId.get(brandId);
                const remove = locked.includes(brandId)
                    ? undefined
                    : () => onChange(brandIds.filter((id) => id !== brandId));
                return brand ? (
                    <BrandChip key={brandId} brand={brand} onRemove={saving ? undefined : remove} />
                ) : (
                    <span
                        key={brandId}
                        className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-mono text-xs"
                    >
                        {brandId}
                        {remove && !saving && (
                            <button type="button" onClick={remove} aria-label={`Remove brand ${brandId}`}>
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                );
            })}
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7"
                disabled={saving}
                onClick={() => setPickerOpen(true)}
            >
                <Plus className="h-3.5 w-3.5" />
                Add brand
            </Button>
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add a brand</DialogTitle>
                        <DialogDescription>Another brand this document is for.</DialogDescription>
                    </DialogHeader>
                    {pickerOpen && (
                        <BrandSearchList
                            brands={brands}
                            exclude={Object.fromEntries(brandIds.map((id) => [id, 'already listed']))}
                            onSelect={(brand) => {
                                onChange([...brandIds, brand.id]);
                                setPickerOpen(false);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

/** "Through Microwave ovens, it also reaches Freestanding Oven, flagged." per family the types touch. */
const TypeFamilyNotes: FC<{ typeIds: EquipmentTypeId[] }> = ({ typeIds }) => {
    const families = useAsyncAtomValue(equipmentTypeFamiliesState, []);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const title = (typeId: EquipmentTypeId) => types.find((type) => type.id === typeId)?.title ?? typeId;
    const reach = familyReach(typeIds, families);
    if (reach.length === 0) return null;
    return (
        <div className="space-y-0.5 text-xs text-muted-foreground">
            {reach.map(({ family, others }) => (
                <div key={family.id}>
                    Through {family.name}, it also reaches {others.map(title).join(', ')}, flagged.
                </div>
            ))}
        </div>
    );
};

/** The brands and types on screen that the stored document does not list yet, as a phrase. */
function useScopeAdditions(value: DocumentScope, stored: DocumentScope | undefined): string | undefined {
    const brands = useAsyncAtomValue(allBrandsState, []);
    const byId = useMemo(() => brandsById(brands), [brands]);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    if (!stored) return undefined;
    const brandNames = value.brandIds
        .filter((id) => !stored.brandIds.includes(id))
        .map((id) => {
            const brand = byId.get(id);
            return brand?.displayName ?? brand?.canonicalName ?? id;
        });
    const typeTitles = value.typeIds
        .filter((id) => !stored.typeIds.includes(id))
        .map((id) => types.find((type) => type.id === id)?.title ?? id);
    const added = [...brandNames, ...typeTitles];
    return added.length > 0 ? added.join(', ') : undefined;
}
